import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// The assessment board. `stateCounts` drives the views strip, and shares the current
// type/organization filters but not the state one, so each tab shows what the rest of
// the filters would leave in it.
// `organizationId` comes from the shell's Business reviews filter, not from this screen —
// it narrows the whole area at once, so it is an argument rather than something the screen
// sets. Everything else in `filters` belongs to this screen alone.
export function useAssessments(organizationId = "") {
  const [assessments, setAssessments] = useState([]);
  const [stateCounts, setStateCounts] = useState({});
  const [kpis, setKpis] = useState(null);
  const [filters, setFilters] = useState({ state: "", type: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(
        Object.entries({ ...filters, organization_id: organizationId }).filter(([, v]) => v)
      ).toString();
      const data = await api.get(`s4/api/assessments${qs ? `?${qs}` : ""}`);
      setAssessments(data?.assessments || []);
      setStateCounts(data?.stateCounts || {});
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the assessments.");
    } finally {
      setLoading(false);
    }
  }, [filters, organizationId]);

  useEffect(() => { load(); }, [load]);

  // The strip still ignores this screen's own filters — it would be noise to have it
  // twitch on every keystroke in the search box. It does follow the organization filter,
  // because tenant-wide totals sitting above a board narrowed to one client read as the
  // filter having failed rather than as two different scopes.
  useEffect(() => {
    (async () => {
      try {
        const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
        setKpis(await api.get(`s4/api/assessments/kpis${qs}`));
      } catch { /* the strip simply does not render */ }
    })();
  }, [organizationId]);

  const create = useCallback(async (payload) => {
    const data = await api.post("s4/api/assessments", payload);
    await load();
    return data;
  }, [load]);

  const setState = useCallback(async (id, state) => {
    try { await api.patch(`s4/api/assessments/${id}`, { state }); await load(); }
    catch (e) { setError(e.message || "Could not change that status."); }
  }, [load]);

  const remove = useCallback(async (id) => {
    await api.del(`s4/api/assessments/${id}`);
    await load();
  }, [load]);

  // Clone is the ordinary create path with clone_from set, not a second implementation:
  // that path already copies the categories and items WITH their ratings, which is the
  // whole point — next quarter's review starts from last quarter's answers and you change
  // what moved. A separate endpoint would be a second thing to keep in step with it.
  //
  // The new assessment is always a draft, whatever the source was: a clone of a published
  // review is work in progress, not something a client has been shown.
  const clone = useCallback(async (source, { title, assessment_date }) => {
    const data = await api.post("s4/api/assessments", {
      organization_id: source.organization_id,
      type: source.type,
      title,
      assessment_date,
      clone_from: source.id,
    });
    await load();
    return data;
  }, [load]);

  return { assessments, stateCounts, kpis, filters, setFilters, loading, error, reload: load, create, clone, remove, setState };
}
